/**
 * Reusable collapsible + vertically resizable shell for inbox composers.
 */
import { useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { ChevronDown, ChevronRight, GripHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCollapsibleComposer } from "@/components/inbox/useCollapsibleComposer";
import { useResizablePanel } from "@/hooks/useResizablePanel";

const KEYBOARD_STEP = 16;

function applyResizeKey(event, { height, setHeight, minHeight, getMaxHeight }) {
  const actions = {
    ArrowUp: () => setHeight(height + KEYBOARD_STEP),
    ArrowDown: () => setHeight(height - KEYBOARD_STEP),
    Home: () => setHeight(minHeight),
    End: () => setHeight(getMaxHeight()),
  };
  const action = actions[event.key];
  if (!action) return;
  event.preventDefault();
  action();
}

function ComposerResizeHandle({
  dragging,
  onResizePointerDown,
  onResizeKeyDown,
  testId,
}) {
  return (
    <div className="relative h-2 shrink-0">
      <button
        type="button"
        aria-label="Resize reply composer"
        onPointerDown={onResizePointerDown}
        onKeyDown={onResizeKeyDown}
        className={cn(
          "absolute inset-0 m-0 h-2 w-full cursor-row-resize touch-none border-0 bg-transparent p-0",
          "hover:bg-slate-100 active:bg-indigo-50",
          dragging && "bg-indigo-50",
        )}
        data-testid={`${testId}-resize-handle`}
        title="Drag to resize"
      />
      <span
        className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-300"
        aria-hidden
      >
        <GripHorizontal size={14} strokeWidth={2} />
      </span>
    </div>
  );
}

ComposerResizeHandle.propTypes = {
  dragging: PropTypes.bool,
  onResizePointerDown: PropTypes.func,
  onResizeKeyDown: PropTypes.func,
  testId: PropTypes.string,
};

function ComposerHeaderMeta({ statusLabel, charCount }) {
  return (
    <>
      {statusLabel ? (
        <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 px-1.5 py-0.5 text-[10px] font-semibold shrink-0">
          {statusLabel}
        </span>
      ) : null}
      {typeof charCount === "number" && charCount > 0 ? (
        <span className="text-[10px] text-slate-400 shrink-0 tabular-nums">
          {charCount.toLocaleString()} chars
        </span>
      ) : null}
    </>
  );
}

ComposerHeaderMeta.propTypes = {
  statusLabel: PropTypes.string,
  charCount: PropTypes.number,
};

export function CollapsibleComposerShell({
  title,
  statusLabel = "Draft",
  charCount = null,
  storageKey = "",
  forceExpandKey = "",
  defaultExpanded = true,
  expandOnMount = false,
  expanded: controlledExpanded,
  onExpandedChange,
  resizable = true,
  defaultHeight = 160,
  minHeight = 120,
  maxRatio = 0.5,
  className,
  headerClassName,
  testId = "collapsible-composer",
  children,
}) {
  const panelRef = useRef(null);

  const uncontrolled = useCollapsibleComposer({
    storageKey,
    forceExpandKey,
    defaultExpanded,
    expandOnMount,
  });

  const isControlled = typeof controlledExpanded === "boolean";
  const expanded = isControlled ? controlledExpanded : uncontrolled.expanded;

  const setExpanded = (next) => {
    if (isControlled) {
      onExpandedChange?.(next);
      return;
    }
    uncontrolled.setExpanded(next);
  };

  const toggle = () => setExpanded(!expanded);

  const getMaxHeight = useCallback(() => {
    const parent = panelRef.current?.parentElement;
    if (parent?.clientHeight) {
      return Math.floor(parent.clientHeight * maxRatio);
    }
    return Math.floor((typeof window !== "undefined" ? window.innerHeight : 800) * maxRatio);
  }, [maxRatio]);

  const heightKey = storageKey ? `height:${storageKey}` : "";
  const { height, setHeight, dragging, onResizePointerDown } = useResizablePanel({
    storageKey: heightKey,
    defaultHeight,
    minHeight,
    maxRatio,
    enabled: resizable,
    getMaxHeight,
  });

  const onResizeKeyDown = (event) => {
    if (!resizable) return;
    applyResizeKey(event, { height, setHeight, minHeight, getMaxHeight });
  };

  const showResizeHandle = resizable && expanded;
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div
      ref={panelRef}
      className={cn(
        "shrink-0 border-t border-slate-200 bg-white flex flex-col min-h-0",
        dragging && "select-none",
        className,
      )}
      style={showResizeHandle ? { height } : undefined}
      data-testid={testId}
      data-expanded={expanded ? "true" : "false"}
      data-dragging={dragging ? "true" : "false"}
    >
      {showResizeHandle ? (
        <ComposerResizeHandle
          dragging={dragging}
          onResizePointerDown={onResizePointerDown}
          onResizeKeyDown={onResizeKeyDown}
          testId={testId}
        />
      ) : null}

      <button
        type="button"
        onClick={toggle}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 transition-colors shrink-0",
          headerClassName,
        )}
        aria-expanded={expanded}
        data-testid={`${testId}-toggle`}
      >
        <span className="text-slate-500 shrink-0" aria-hidden>
          <Chevron size={14} />
        </span>
        <span className="text-[12px] font-semibold text-slate-900 truncate min-w-0">
          {title}
        </span>
        <ComposerHeaderMeta statusLabel={statusLabel} charCount={charCount} />
        <span className="ml-auto text-[10.5px] text-slate-500 shrink-0">
          {expanded ? "Collapse" : "Expand"}
        </span>
      </button>

      <div
        className={cn(
          "grid min-h-0 flex-1",
          dragging ? "" : "transition-[grid-template-rows] duration-200 ease-out",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
        data-testid={`${testId}-body`}
      >
        <div className="overflow-hidden min-h-0 flex flex-col h-full">
          <div
            className={cn(
              "min-h-0 flex-1 flex flex-col h-full",
              dragging ? "" : "transition-opacity duration-200",
              expanded ? "opacity-100" : "opacity-0 pointer-events-none",
            )}
            aria-hidden={!expanded}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

CollapsibleComposerShell.propTypes = {
  title: PropTypes.string.isRequired,
  statusLabel: PropTypes.string,
  charCount: PropTypes.number,
  storageKey: PropTypes.string,
  forceExpandKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultExpanded: PropTypes.bool,
  expandOnMount: PropTypes.bool,
  expanded: PropTypes.bool,
  onExpandedChange: PropTypes.func,
  resizable: PropTypes.bool,
  defaultHeight: PropTypes.number,
  minHeight: PropTypes.number,
  maxRatio: PropTypes.number,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  testId: PropTypes.string,
  children: PropTypes.node,
};

export { useCollapsibleComposer };
